import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, FileText, Scale, AlertTriangle, CheckCircle, 
  XCircle, Gavel, CreditCard, RefreshCcw, Server 
} from "lucide-react";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    // MODIFIED: Added `font-legal` to enforce the same readable typography hierarchy as the FAQ and Privacy Policy pages.
    <div className="min-h-screen bg-background font-legal">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/30">
        <div className="container mx-auto px-6 py-4">
          <button 
            onClick={() => navigate("/#footer")}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-sans text-sm md:text-base cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3.5 rounded-2xl bg-primary/10 border border-primary/20 shadow-sm">
                <FileText className="w-8 h-8 md:w-10 md:h-10 text-primary" />
              </div>
              <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight text-foreground">
                Terms of Service
              </h1>
            </div>
            {/* MODIFIED: Increased font size and line height for desktop/laptop reading comfort */}
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl leading-relaxed">
              Please read these terms carefully. They outline your rights, our obligations, and the limitations of our safety service.
            </p>
            <p className="text-sm md:text-base text-muted-foreground/70 mt-6 font-sans font-medium">
              Effective Date: July 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 pb-28">
        <div className="container mx-auto px-6">
          {/* MODIFIED: Increased spacing between sections (space-y-16 to space-y-20) for clean visual separation on desktop monitors */}
          <div className="max-w-4xl mx-auto space-y-16 md:space-y-20">
            
            {/* Section 1: Acceptance & Legal Age Gate */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                {/* MODIFIED: Upgraded icon container padding, borders, and hover transitions */}
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Scale className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    1. Acceptance of Terms & Eligibility
                  </h2>
                  {/* MODIFIED: Standardized text scale to text-base/text-[16.5px] with leading-8 for effortless reading */}
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      By accessing or using Ping After, a specialized safety platform operated by VANT, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not use our service.
                    </p>
                    <p>
                      <strong className="text-foreground font-semibold">Age Requirement:</strong> You must be at least 18 years of age to create an account and use Ping After. By registering, you represent and warrant that you meet this age requirement and have the legal capacity to enter into a binding contract.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: CRITICAL for Safety Apps (Not an Emergency Service) */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    2. Service Limitations (Not an Emergency Service)
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      Ping After is an automated, supplementary communication tool designed to dispatch alert packages to your predefined contacts if an active trip deadline expires without your confirmation. <strong className="text-foreground font-semibold">It is explicitly NOT a replacement for official emergency services (e.g., 911, 112, Police, Fire, or Medical dispatch).</strong>
                    </p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li>We do not actively monitor your real-time location or personal safety in the background.</li>
                      <li>We do not dispatch human emergency personnel or contact local authorities on your behalf.</li>
                      <li>Alert delivery relies entirely on third-party telecommunications networks (ISPs, cellular carriers, SMS gateways, and email servers) and cannot be guaranteed with 100% certainty.</li>
                      <li><strong className="text-foreground font-semibold">Dead Switch (Heir System):</strong> The automated transmission of your historical trip records to a designated Trustee is an informational safety feature only. It carries no legal status as a digital will, formal estate instruction, or legal proof of life/death.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Payments & Merchant of Record */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    3. Payments & Merchant of Record
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      Our order process is conducted by our authorized online reseller and Merchant of Record (MoR). The MoR manages all transaction processing, local tax compliance, customer service inquiries regarding billing, and returns.
                    </p>
                    <p>
                      By purchasing a Pro tier subscription, you explicitly agree to our MoR's Terms and Conditions. Subscriptions automatically renew at the end of each billing cycle unless canceled prior to the renewal date through your account settings.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Refund & Cancellation Policy (Binary Zero-Usage Rule) */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <RefreshCcw className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    4. Refund & Cancellation Policy
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      We offer a 14-day money-back guarantee for initial Pro subscription purchases, governed by a strictly defined <strong className="text-foreground font-semibold">Zero-Usage Condition:</strong>
                    </p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li>
                        Refunds are issued <em className="text-foreground font-medium">only</em> if you have not consumed any premium digital resources during the 14-day period. Specifically, eligibility requires that exactly <strong className="text-foreground font-semibold">0 SMS alert credits</strong> have been triggered and exactly <strong className="text-foreground font-semibold">0 Bytes of emergency media attachments</strong> have been uploaded to our cloud storage.
                      </li>
                      <li>
                        Once any premium feature is exercised, the service is classified as a rendered digital good under applicable consumer laws, and the subscription becomes strictly non-refundable.
                      </li>
                    </ul>
                    <p className="mt-6 font-semibold text-foreground text-lg">Anti-Abuse & Chargeback Protection:</p>
                    <p>
                      Initiating a fraudulent chargeback or payment dispute for rendered services after consuming premium features will result in immediate termination of your account, permanent blacklisting of your IP address and credentials across all VANT systems, and potential contestation of the dispute using system audit logs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: User Responsibilities & TCPA/Spam Defense */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <AlertTriangle className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    5. User Responsibilities & Contact Consent
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      Ping After transmits automated communications to third parties designated by you. You acknowledge and agree that:
                    </p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li>
                        <strong className="text-foreground font-semibold">You are the Data Controller:</strong> You represent and warrant that you have obtained prior explicit consent from all emergency contacts and designated Trustees before submitting their phone numbers or email addresses to our platform.
                      </li>
                      <li>
                        <strong className="text-foreground font-semibold">Opt-Out Rights:</strong> All alert messages include automated opt-out or abuse reporting links (e.g., "Report Spam / Stop Alerts"). If a contact exercises their right to opt out, our systems will permanently block future transmissions to that destination, and we are not liable for failed emergency alerts resulting from a contact's self-imposed block.
                      </li>
                      <li>
                        You will not misuse the platform to intentionally generate false alarms, spam telecommunication gateways, or harass third parties.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Third-Party Infrastructure & Availability */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Server className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    6. Third-Party Infrastructure & Availability
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      To deliver global scalability and high availability, Ping After integrates with industry-standard third-party infrastructure providers (including cloud object storage networks, geolocation and mapping services, and enterprise SMS/Email routing gateways).
                    </p>
                    <p>
                      While we enforce strict service-level standards with our vendors, we do not guarantee uninterrupted, error-free operation of third-party APIs or carrier networks. Scheduled maintenance or downstream network outages may occasionally impact service latency.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 7: Limitations of Liability & Indemnification */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <XCircle className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    7. Limitation of Liability & Indemnification
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      To the maximum extent permitted by applicable law, VANT, its developers, and its affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of:
                    </p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li>Your access to, use of, or inability to access or use the safety system.</li>
                      <li>Failures, carrier delays, or routing drops by third-party communication providers.</li>
                      <li>Any action, inaction, or delayed response taken by your emergency contacts or Trustees upon receiving (or failing to receive) an automated alert package.</li>
                    </ul>
                    <p className="mt-6">
                      <strong className="text-foreground font-semibold">Indemnification:</strong> You agree to defend, indemnify, and hold harmless VANT from any claims, damages, fines, or legal fees arising from your failure to obtain consent from your emergency contacts, intentional false alarms, or any dispatch of local authorities triggered by your use of the service.
                    </p>
                    <div className="p-5 rounded-2xl bg-secondary/30 border border-border/40 mt-4 text-xs md:text-sm uppercase tracking-wider text-muted-foreground font-semibold leading-relaxed">
                      The service is provided strictly on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 8: Governing Law (Delaware, USA fallback for SaaS) */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Gavel className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    8. Governing Law & Jurisdiction
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      While transactional and billing processing are governed by the jurisdictional terms of our Merchant of Record, these Terms of Service and your operational relationship with VANT shall be governed by and construed in accordance with the laws of the State of Delaware, USA, without regard to its conflict of law principles.
                    </p>
                    <p>
                      Any non-transactional dispute or claim arising out of or relating to these terms or the use of Ping After shall be resolved exclusively in the appropriate courts of jurisdiction.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border/30">
        <div className="container mx-auto px-6 text-center text-sm text-muted-foreground font-sans">
          © {new Date().getFullYear()} VANT. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;